import i18nFactory from '@yandex-int/i18n';
import { isBoolean, isEmpty, isFinite, isInteger } from 'lodash';
import moment from 'moment';

import { EnumSetItem, FilterExpressionValue } from '../../typings/filter';
import {
    StringInputField,
    StringInputFieldErrors
} from '../../typings/freelancers';
import { parseNumber } from '../FormatHelpers/FormatHelpers';

import * as keyset from './ValidationHelpers.i18n';

const i18n = i18nFactory(keyset);

export function validateEnumValue(
    enumObj: object
): (value: FilterExpressionValue) => boolean {
    return function(value: FilterExpressionValue): boolean {
        return (value.value as EnumSetItem[]).every(
            item => item.key in enumObj
        );
    };
}

export function validatePositiveNumberSetValue(
    value: FilterExpressionValue
): boolean {
    const arr = value.value as string[];
    return (
        arr.length > 0 &&
        arr.every(item => {
            const num = Number(item);

            return isInteger(num) && num > 0;
        })
    );
}

export function validatePositiveDoubleNumberValue(
    value: FilterExpressionValue
): boolean {
    return isFinite(value.value) && value.value >= 0;
}

export function validatePositiveIntegerNumberValue(
    value: FilterExpressionValue
): boolean {
    return isInteger(value.value) && value.value >= 0;
}

export function validateBooleanValue(value: FilterExpressionValue): boolean {
    return isBoolean(value.value);
}

export function validateDateValue(value: FilterExpressionValue): boolean {
    return moment.isMoment(value.value);
}

export function validateTextValue(value: FilterExpressionValue): boolean {
    return true;
}

export function validatePercent(
    value: string,
    [minLimit, maxLimit]: number[]
): string | undefined {
    const num = parseNumber(value);

    if (isEmpty(value)) {
        return i18n('emptyMessage');
    }

    if (isNaN(num)) {
        return i18n('nanMessage');
    }

    if (!isInteger(num)) {
        return i18n('integerMessage');
    }

    if (num > maxLimit) {
        return i18n('tooBig', { minLimit, maxLimit });
    } else if (num < minLimit) {
        return i18n('tooLittle', { minLimit, maxLimit });
    } else {
        return undefined;
    }
}

const PHONE_PTRN = /^\+?[0-9\-\s\(\)]+$/;
const PHONE_REPLACE_PTRN = /\D/g;
const PHONE_NUMBER_MIN_LENGTH = 9;
const PHONE_NUMBER_MAX_LENGTH = 15;
const PHONE_SYMBOLS_MAX_LENGTH = 255;

export const validatePhone = (phone: StringInputField): StringInputField => {
    const trimmedContactVal = phone.inputVal.trim();
    const phoneNumbers = trimmedContactVal.replace(PHONE_REPLACE_PTRN, '');

    return {
        inputVal: trimmedContactVal,
        error: !trimmedContactVal
            ? StringInputFieldErrors.MANDATORY_FIELD
            : trimmedContactVal.match(PHONE_PTRN) &&
              phoneNumbers.length >= PHONE_NUMBER_MIN_LENGTH &&
              phoneNumbers.length <= PHONE_NUMBER_MAX_LENGTH &&
              trimmedContactVal.length <= PHONE_SYMBOLS_MAX_LENGTH
            ? undefined
            : StringInputFieldErrors.INCORRECT_FORMAT
    };
};

export const isIPValid = (ip: string): boolean => {
    const ipV4RegExpStr =
        '^([0-9]{1,3})\\.([0-9]{1,3})\\.([0-9]{1,3})\\.([0-9]{1,3})$';
    const ipParts = ip.match(new RegExp(ipV4RegExpStr, 'i'));

    if (!ipParts) {
        return false;
    }

    for (let i = 1; i <= 4; i++) {
        const ipPart = parseInt(ipParts[i], 10);
        const isValidPart =
            ipPart <= 255 && !(ipPart > 0 && ipParts[i][0] === '0');

        if (!isValidPart) {
            return false;
        }
    }

    return true;
};
